import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Checkbox,
  createStyles,
  FormControlLabel,
  Grid,
  makeStyles,
  RadioGroup,
  Theme,
  Typography,
} from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "components/Dialog";
import TextField from "components/TextField";
import HTTP_STATUS_CODES from "constants/http-status-code";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { ParcelOrderStoreContext } from "contexts/ParcelsDataContext";
import { ParcelActionType } from "data/types";
import {
  ParcelRadio,
  ParcelRadioD,
  ParcelRadioL,
  ParcelRadioM,
  ParcelRadioXL,
  ParcelRadioXXL,
} from "domains/Task/components/Job/Radio";
import { Job, Order, Parcel } from "generated/graphql";
import { ALL_CLIENT_REF_ADDRESS_BY_CLIENT_ID } from "graphql/clientRef/queries";
import { PARCELS_UPDATE } from "graphql/Parcel/mutations";
import { useCurrentClient } from "hooks";
import { DocumentIcon, SizedIconL, SizedIconM, SizedIconS, SizedIconXL, SizedIconXXL } from "icons";
import _ from "lodash";
import React from "react";
import "styled-components/macro";
import Autocomplete from "ui/Autocomplete";
import { validJobTypes } from "./NewParcel";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    icons: {
      width: "80px !important",
      height: "80px !important",
      [theme.breakpoints.down("xs")]: { width: "53px !important" },
    },
    grid: {
      height: "52vh",
    },
    titleDialog: {
      paddingLeft: "30px",
      paddingTop: "16px",
      fontSize: "20px",
      fontWeight: 900,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "normal",
      letterSpacing: "normal",
      color: "#565c5e",
    },
    radioGroup: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
  })
);

interface IProps {
  order?: Order;
  open?: boolean;
  handleClose?: () => void;
  handleUpdate?: (parcel: Parcel) => void;
  refetch?: () => void;
  parcel: Parcel;
  job?: Job;
}

export function UpdateParcel({ open, job, handleClose, refetch, order, parcel }: IProps) {
  const [isFormSubmited, setFormSubmited] = React.useState(false);
  const [formValues, setFormValues] = React.useState(parcel);
  const { text } = useText();
  const [addresses, setAdresses] = React.useState([]);
  const [differentPickupAddresses, setDifferentPickupAddresses] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const dispatchAlert = useAlertDispatch();
  const styles = useStyles();
  const [updateParcels] = useMutation(PARCELS_UPDATE);
  const [, dispatcher] = React.useContext(ParcelOrderStoreContext);

  const [allClientRefAddressByClientId, { loading, data }] = useLazyQuery(ALL_CLIENT_REF_ADDRESS_BY_CLIENT_ID);
  const { client } = useCurrentClient();

  const isValidJobType = React.useMemo<boolean>(() => validJobTypes.includes(job?.job_type), [job?.job_type]);

  const handleCheckboxChange = () => {
    setDifferentPickupAddresses(!differentPickupAddresses);
  };

  const handleOnInputChange = (event: any, newInputValue: React.SetStateAction<string>) => {
    setInputValue(newInputValue);
  };

  const handleOnPickupAddressesChange = (event: any, newValue: any) => {
    const pickupMoreInfoObject = {
      firstName: newValue?.firstname,
      lastName: newValue?.lastname,
      phoneNumber: newValue?.phone_number,
    };
    setFormValues({
      ...formValues,
      pickupAddress: _.omit(newValue, ["default", "email", "lastname", "phone_number", "address.country"]),
      pickupMoreInfo: pickupMoreInfoObject,
    });
  };
  React.useEffect(() => {
    allClientRefAddressByClientId({ variables: { params: { clientID: client?._id } } });
  }, [open, differentPickupAddresses]);

  React.useEffect(() => {
    const result = data?.results;
    if (result?.code === HTTP_STATUS_CODES.OK) {
      const addresses = result?.data.map((address: any) => address);
      setAdresses(addresses);
    }
  }, [data]);
  const handleChange = React.useCallback((event: any) => {
    event?.persist();
    setFormValues((prev) => ({
      ...prev,
      [event?.target?.name]: event?.target?.value,
    }));
  }, []);

  React.useEffect(() => {
    if (parcel != null) {
      setFormValues({ ...parcel });
    }
  }, [parcel]);

  const handleUpdateParcels = async () => {
    await updateParcels({
      variables: {
        input: {
          parcelID: formValues?._id,
          parcel: {
            parcel_size: formValues?.parcel_size,
            parcel_value: Number(formValues?.parcel_value),
            parcel_additional_info: formValues?.parcel_additional_info,
            parcel_description: formValues?.parcel_description,
            parcel_client_reference: formValues?.parcel_client_reference,
            parcel_amount_to_collect: Number(formValues?.parcel_amount_to_collect),
          },
        },
      },
    })
      .then((result) => {
        const { data, message } = result?.data?.updateParcel;

        dispatchAlert({
          severity: data ? "success" : "error",
          message,
        });
        dispatcher({
          type: ParcelActionType.UpdateParcels,
          data: [{ parcelID: formValues?._id }],
        });
        setTimeout(handleClose, 1500);
      })
      .catch((error) => {
        dispatchAlert({
          severity: "error",
          message: error.message,
        });
        setTimeout(handleClose, 1500);
      });
    refetch();
  };

  const handleConfirm = () => {
    setFormSubmited(true);
    handleUpdateParcels();
    const { parcel_value, parcel_amount_to_collect } = formValues;
    if (parcel_value < 0 || parcel_amount_to_collect < 0) return;
    setFormSubmited(false);
    handleClose();
  };

  return (
    <Dialog
      customDialogTitle={
        <Typography variant="h3" className={styles.titleDialog}>
          {text.all.update_parcel}
          <span style={{ color: "#399ebf" }}>{order?.order_receiver?.receiver_firstname}</span>
        </Typography>
      }
      open={open}
      confirmButtonTitle="Update"
      handleClose={() => handleClose()}
      handleConfirm={handleConfirm}
      maxWidth="md"
    >
      <Grid item xs={12}>
        <label
          css={`
            color: #707070;
          `}
        >
          Size
        </label>
      </Grid>
      <Grid container spacing={4} className={styles.grid}>
        <Grid item xs={12}>
          <RadioGroup
            row
            aria-label="parcel_size"
            name="parcel_size"
            value={formValues?.parcel_size}
            onChange={handleChange}
            className={styles.radioGroup}
          >
            <Tooltip title="Document, couriel">
              <ParcelRadioD value="D" icon={<DocumentIcon />} checkedIcon={<DocumentIcon color="#399ebf" />} />
            </Tooltip>
            <Tooltip title="S (max 5kg) ex: Laptop, cell phone, cloth, soap jar">
              <ParcelRadio
                className={styles.icons}
                value="S"
                icon={<SizedIconS />}
                checkedIcon={<SizedIconS color="#399ebf" />}
              />
            </Tooltip>
            <Tooltip title="M (max 30kg) ex: Small electrical appliances (microwave, fridge bar, cardboard, TV)">
              <ParcelRadioM
                className={styles.icons}
                value="M"
                icon={<SizedIconM />}
                checkedIcon={<SizedIconM color="#399ebf" />}
              />
            </Tooltip>
            <Tooltip title="L (max 60kg) ex: Big electrical appliances (fridge, gas cooker, washing machine)">
              <ParcelRadioL
                className={styles.icons}
                value="L"
                icon={<SizedIconL />}
                checkedIcon={<SizedIconL color="#399ebf" />}
              />
            </Tooltip>
            <Tooltip title="XL (max 100kg) ex: Bedroom">
              <ParcelRadioXL
                className={styles.icons}
                value="XL"
                icon={<SizedIconXL />}
                checkedIcon={<SizedIconXL color="#399ebf" />}
              />
            </Tooltip>
            <Tooltip title="XL (max 100kg) ex: Bedroom">
              <ParcelRadioXXL
                className={styles.icons}
                value="XXL"
                icon={<SizedIconXXL />}
                checkedIcon={<SizedIconXXL color="#399ebf" />}
              />
            </Tooltip>
          </RadioGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Description"
            name="parcel_description"
            value={formValues?.parcel_description}
            onChange={handleChange}
            error={isFormSubmited && formValues?.parcel_description.length === 0}
            helperText={isFormSubmited && formValues?.parcel_description.length === 0 ? "Description is required" : ""}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Client reference (external)"
            name="parcel_client_reference"
            value={formValues?.parcel_client_reference}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Price"
            type="number"
            name="parcel_value"
            value={formValues?.parcel_value}
            onChange={handleChange}
            error={isFormSubmited && (String(formValues?.parcel_value).length === 0 || formValues?.parcel_value) < 0}
            helperText={
              isFormSubmited && (String(formValues?.parcel_value).length === 0 || formValues?.parcel_value) < 0
                ? "Value is required"
                : ""
            }
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Amount to collect"
            name="parcel_amount_to_collect"
            value={formValues?.parcel_amount_to_collect}
            onChange={handleChange}
            error={
              isFormSubmited &&
              (String(formValues?.parcel_amount_to_collect).length === 0 || formValues?.parcel_amount_to_collect) < 0
            }
            helperText={
              isFormSubmited &&
              (String(formValues?.parcel_amount_to_collect).length === 0 || formValues?.parcel_amount_to_collect) < 0
                ? "Amount to collect is required"
                : ""
            }
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={text.all.additionals_infos}
            name="parcel_additional_info"
            value={formValues?.parcel_additional_info}
            onChange={handleChange}
          />
        </Grid>

        {!isValidJobType && (
          <>
            <Grid item xs={12} style={{ padding: "0 16px" }}>
              <FormControlLabel
                label={text.parcel.different_pick_up_addresses}
                control={<Checkbox checked={differentPickupAddresses} onChange={handleCheckboxChange} />}
              />
            </Grid>

            <Grid item xs={12} style={{ marginBottom: "40px" }}>
              <Autocomplete
                disabled={!differentPickupAddresses}
                name={text.parcel.different_pick_up_addresses}
                options={addresses}
                value={formValues?.pickupAddress}
                onChange={handleOnPickupAddressesChange}
                inputValue={inputValue}
                onInputChange={handleOnInputChange}
                label={text.parcel.different_pick_up_addresses}
                getOptionLabel={(option: any) => `${option?.address?.address}`}
                renderInput={(params: any) => <TextField {...params} variant="outlined" />}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Dialog>
  );
}

export default UpdateParcel;
